import { getEnvironment } from "configuration/endpoints/environments";
import { FIVE_MINUTES_MS, useReloadIntervalToken } from "helpers";
import { LoadingState } from "model/Types/loading";
import React from "react";
import { useAccountId, useOrganizationId } from "redux/auth/selectors";
import { actions as creativesActions } from "redux/creatives/actions";
import {
  getErrorLoadingActiveCreatives,
  getLastActiveCreativesFetch,
  getLastArchivedCreativesFetch,
} from "redux/creatives/selectors";
import { useSelector } from "redux/hooks";
import { useAppDispatch } from "redux/store";

type TReturn = {
  status: LoadingState;
  error: string | null;
};

const shouldLoadApprovalStatusesByEnv = getEnvironment().fetchCreativeApprovals;

function useCreativesDataProvider(args?: {
  disableReload?: boolean;
  shouldLoadApprovalStatuses?: boolean;
  shouldLoadArchived?: boolean;
}): TReturn {
  const {
    disableReload = false,
    shouldLoadApprovalStatuses = false,
    shouldLoadArchived = true,
  } = args || {};
  const dispatch = useAppDispatch();

  useReloadIntervalToken({
    allowReload: true,
    intervalLength: FIVE_MINUTES_MS + 1000, // NOTE: this is 1s more than the actual interval as it loads first
  });

  const lastActiveCreativesFetch = useSelector(getLastActiveCreativesFetch);
  const lastArchivedCreativesFetch = useSelector(getLastArchivedCreativesFetch);
  const errorLoadingActiveCreatives = useSelector(
    getErrorLoadingActiveCreatives
  );
  const organizationId = useOrganizationId();
  const accountId = useAccountId();

  const [error, setError] = React.useState<string | null>(null);
  const [status, setStatus] = React.useState<LoadingState>(
    LoadingState.initial
  );

  React.useEffect(() => {
    const now = +new Date();

    if (disableReload || now - lastActiveCreativesFetch > FIVE_MINUTES_MS) {
      setStatus(LoadingState.loading);
      dispatch(
        creativesActions.loadActiveCreatives(
          shouldLoadApprovalStatuses && shouldLoadApprovalStatusesByEnv
        )
      );
    }

    if (
      shouldLoadArchived &&
      (disableReload || now - lastArchivedCreativesFetch > FIVE_MINUTES_MS)
    ) {
      dispatch(creativesActions.loadArchivedCreatives());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    organizationId,
    accountId,
    shouldLoadApprovalStatuses,
    shouldLoadApprovalStatusesByEnv,
    shouldLoadArchived,
  ]);

  React.useEffect(() => {
    if (errorLoadingActiveCreatives) {
      setError("An error loading creatives has occurred");
      setStatus(LoadingState.failed);
    }
  }, [errorLoadingActiveCreatives]);

  React.useEffect(() => {
    if (lastActiveCreativesFetch > 0) {
      setStatus(LoadingState.loaded);
    }
  }, [lastActiveCreativesFetch]);

  return { status, error };
}

export default useCreativesDataProvider;
